import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PhotoGalleryTableComponent from "../../../components/admin/photo-gallery-manager/PhotoGalleryTable";
import { PhotoGalleryAddDrawer } from "../../../components/admin/photo-gallery-manager/PhotoGalleryAddDrawer";
import { PhotoGalleryAssignDialog } from "../../../components/admin/photo-gallery-manager/PhotoGalleryAssignDialog";
import { SearchRecords } from "../../../components/Common/SearchRecords";
import AutoCompleteSelect from "../../../components/Common/AutoCompleteSelect";
import {
    useGetGalleryFolderListQuery,
    useGetGalleryListQuery,
} from "../../../store/masters/mastersApis";
import { defaultPaginate, PERMISSIONS } from "../../../helpers/constants";
import HasPermission from "../../../components/Common/HasPermission";

const PhotoGalleryManagerContainer = (props) => {
    const {
        showDrawer,
        formType,
        initialValues,
        pagination,
        handlePaginationState,
        changeMastersInitialState,
        navigate,
    } = props;
    const [assignDialog, setAssignDialog] = useState({
        show: false,
        initialValues: { _id: "", title: "", folder_id: "" },
    });

    const { isLoading, data: gallery, isFetching } = useGetGalleryListQuery({
        ...pagination,
        type: "image",
    });
    const { data: folderOptionResponse } = useGetGalleryFolderListQuery({
        pageNo: 0,
        limit: 500,
        sortBy: 1,
        sortField: "name",
    });

    useEffect(() => {
        handlePaginationState({
            ...defaultPaginate,
            folder_id: "",
        });
    }, [handlePaginationState]);

    const folderOptions = useMemo(
        () =>
            (folderOptionResponse?.result || []).map((folder) => ({
                value: folder._id,
                label: folder.name,
                description: folder.description,
                activityLabel: folder?.activity_data?.name || "",
            })),
        [folderOptionResponse],
    );

    const selectedFolder =
        folderOptions.find((folder) => folder.value === pagination?.folder_id) || null;
    const folderCount = folderOptionResponse?.count || 0;
    const photoCount = gallery?.count || 0;
    const unassignedPhotoCount = (gallery?.result || []).filter((item) => !item?.folder_data?.name).length;
    const summaryCards = [
        {
            title: "Folders",
            value: folderCount,
            helper: "Shared library for photos and videos",
            icon: <FolderRoundedIcon color="success" />,
        },
        {
            title: "Photos",
            value: photoCount,
            helper: selectedFolder ? `Visible in ${selectedFolder.label}` : "Visible in current result set",
            icon: <CollectionsRoundedIcon color="primary" />,
        },
        {
            title: "Unassigned on page",
            value: unassignedPhotoCount,
            helper: "Legacy records that still need a folder",
            icon: <AddPhotoAlternateRoundedIcon color="warning" />,
        },
    ];

    const handlePagination = (setting) => {
        handlePaginationState(setting);
    };

    const handleAddPhotoGallery = (type) => {
        changeMastersInitialState({
            showDrawer: true,
            formType: type,
            initialValues: { folder_id: pagination?.folder_id || "", title: "", url: "" },
        });
    };

    const handleDrawerClose = () => {
        changeMastersInitialState({
            showDrawer: false,
            formType: "",
            initialValues: { folder_id: "", title: "", url: "" },
        });
    };

    const handleFolderFilterChange = (_, value) => {
        handlePaginationState({
            ...pagination,
            pageNo: 0,
            folder_id: value?.value || "",
        });
    };

    const handlePhotoGalleryEdit = (data, type) => {
        const payload = { ...data };
        delete payload.createdAt;
        changeMastersInitialState({
            showDrawer: true,
            formType: type,
            initialValues: {
                ...payload,
                folder_id: payload?.folder_id || payload?.folder_data?._id || "",
            },
        });
    };

    const openAssignDialog = (photo) => {
        setAssignDialog({
            show: true,
            initialValues: {
                _id: photo?._id || "",
                title: photo?.title || "",
                folder_id: photo?.folder_id || photo?.folder_data?._id || "",
            },
        });
    };

    const closeAssignDialog = () => {
        setAssignDialog({
            show: false,
            initialValues: { _id: "", title: "", folder_id: "" },
        });
    };

    return (
        <Stack spacing={2}>
            <Paper
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, rgba(16,124,65,0.08) 0%, rgba(7,27,42,0.02) 100%)",
                }}
            >
                <Stack spacing={3}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Stack spacing={1}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Photo Gallery Management
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Upload photos into shared activity-linked folders instead of a flat
                                    collection, and keep older records organized from the same screen.
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                justifyContent="flex-end"
                                alignItems={{ xs: "stretch", sm: "center" }}
                            >
                                <HasPermission
                                    permissions={[
                                        PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.CREATE,
                                        PERMISSIONS.COMMON_MASTER.VIDEO_GALLERY.CREATE,
                                    ]}
                                    fallback={null}
                                >
                                    <Button
                                        disableElevation
                                        variant="outlined"
                                        startIcon={<SettingsRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 160 }}
                                        onClick={() => navigate("/gallery-folder-master")}
                                    >
                                        Manage Folders
                                    </Button>
                                </HasPermission>
                                <HasPermission
                                    permission={PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.CREATE}
                                    fallback={null}
                                >
                                    <Button
                                        disableElevation
                                        variant="contained"
                                        startIcon={<AddPhotoAlternateRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 150 }}
                                        onClick={() => handleAddPhotoGallery("photo")}
                                    >
                                        Upload Photos
                                    </Button>
                                </HasPermission>
                                <HasPermission
                                    permission={PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.CREATE}
                                    fallback={null}
                                >
                                    <Button
                                        disableElevation
                                        variant="contained"
                                        color="success"
                                        startIcon={<LinkRoundedIcon />}
                                        sx={{ borderRadius: "14px", minWidth: 150 }}
                                        onClick={() => handleAddPhotoGallery("links")}
                                    >
                                        Add Drive Link
                                    </Button>
                                </HasPermission>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "rgba(255,255,255,0.72)",
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <AutoCompleteSelect
                                    id="photo-gallery-folder-filter"
                                    options={folderOptions}
                                    label="Filter by Folder"
                                    onChange={handleFolderFilterChange}
                                    value={selectedFolder}
                                    name="folder_id"
                                    keyname="label"
                                    disableCloseOnSelect={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Chip
                                        color={selectedFolder ? "success" : "default"}
                                        label={
                                            selectedFolder
                                                ? `Folder: ${selectedFolder.label}`
                                                : "Showing all folders"
                                        }
                                        sx={{ borderRadius: "10px" }}
                                    />
                                    {selectedFolder?.activityLabel ? (
                                        <Chip
                                            variant="outlined"
                                            label={`Activity: ${selectedFolder.activityLabel}`}
                                            sx={{ borderRadius: "10px" }}
                                        />
                                    ) : null}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Grid container spacing={2}>
                        {summaryCards.map((card) => (
                            <Grid item xs={12} md={4} key={card.title}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        height: "100%",
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="overline" color="text.secondary">
                                                {card.title}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                {card.value}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {card.helper}
                                            </Typography>
                                        </Box>
                                        {card.icon}
                                    </Stack>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Paper>

            <Paper sx={{ p: 2.5, color: "#071B2A", fontWeight: "400", borderRadius: 3 }} elevation={0}>
                <Grid container justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={0.5}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Photo Records
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    size="small"
                                    color={selectedFolder ? "success" : "default"}
                                    label={selectedFolder ? selectedFolder.label : "All folders"}
                                    sx={{ borderRadius: "8px" }}
                                />
                                {unassignedPhotoCount > 0 ? (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label={`${unassignedPhotoCount} unassigned on this page`}
                                        sx={{ borderRadius: "8px" }}
                                    />
                                ) : null}
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" textAlign={{ xs: "left", md: "right" }}>
                            Folders are now managed separately and reused across both photo and video galleries.
                        </Typography>
                    </Grid>
                </Grid>
                {folderCount === 0 ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        No folders exist yet. Create a folder in Folder Management before uploading new photos.
                    </Alert>
                ) : null}
                {unassignedPhotoCount > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Some older photos on this page are still unassigned to a folder. New uploads already require
                        a folder.
                    </Alert>
                ) : null}
                <PhotoGalleryTableComponent
                    fetching={isFetching}
                    edit={(val, type) => handlePhotoGalleryEdit(val, type)}
                    loading={isLoading}
                    count={gallery?.count || 0}
                    data={gallery?.result || []}
                    pagination={pagination}
                    handlePagination={handlePagination}
                    onAssignFolder={openAssignDialog}
                />
            </Paper>

            <PhotoGalleryAddDrawer
                show={showDrawer}
                close={handleDrawerClose}
                formType={formType}
                initialValues={initialValues}
            />
            <PhotoGalleryAssignDialog
                show={assignDialog.show}
                close={closeAssignDialog}
                initialValues={assignDialog.initialValues}
                folderOptions={folderOptions}
            />
        </Stack>
    );
};

export default PhotoGalleryManagerContainer;
